// 共用訊息窗的全域入口 — 對照 Unity SlotFramework/Script/UI/Dialog/DialogMediator.cs（靜態類別）。
// Unity 端的分工：DialogMediator 持有唯一 Dialog 實例、負責在地化查表與按鈕點擊分派，
// 呼叫端（BaseGameService／GameServerHandler／GameEventDispatcher／RewardPresentationPanelMediator）
// 只認 ShowDialog / ShowError / ShowErrorCode / HideDialog 這幾支靜態方法。本檔為等價的模組級 API。
//
// 對照的 Unity 呼叫慣例（全部走 Dialog_StringTable，本專案 i18n key 前綴 dialog.）：
//   ShowErrorCode(code) → 標題 System_Title、內文 "Error_Code_" + code、格式參數 {0}=code、
//                         單一 OK 鈕、無關閉鈕；按下後發 ErrorLogEvent
//   ShowError(key)      → 標題 System_Title、內文 key、單一 OK 鈕、無關閉鈕；按下後發 ErrorLogEvent
//   餘額不足            → 標題 System_Title、內文 Error_Balance、OK 鈕只關窗（不發 ErrorLogEvent，
//                         GameEventDispatcher 註解明示「Client 端自行判斷的餘額不足視窗不須重整遊戲」）
//   MaxWin              → 標題 System_Title、內文 System_MaxWin（{0}=倍數）、Btn_Confirm 鈕
//
// ErrorLogEvent 的後續行為（唯一訂閱者 MenuWindowManager.OnReturnTo）在 WebGL 下是
// 「有平台 HomeUrl 就導回大廳、沒有就整頁 reload」。本專案沒有平台 HomeUrl（選單的 Home 亦為佔位），
// 故對應為 window.location.reload()——與被替換掉的 ConnectionNoticeDialog 既有行為一致。
//
// [已知差異] Unity 的 ShowError/ShowErrorCode 按鈕文字硬編英文字串 "OK"（同表內有 Btn_Confirm 卻沒用到，
//   屬來源的漏在地化；其 MaxWin 呼叫點就有正確使用 Btn_Confirm）。本移植統一改用 dialog.Btn_Confirm，
//   非英文語系不會突兀地跳出 "OK"。按鈕樣式仍走 Unity 的 fallback：樣式留空＝_styleObjects[0]＝"OK"（btn_start）。

import { t } from '../../../framework/i18n/i18n'
import { logger } from '../../../framework/core/Logger'
import type { DialogAction, SystemDialog } from './SystemDialog'

/** 一次對話框請求（文字以 i18n key 表示，於顯示時才查表，對應 Unity 的 table+key 參數形式）。 */
export interface DialogRequest {
  /** 標題 i18n key（預設 dialog.System_Title）。 */
  readonly titleKey?: string
  /** 內文 i18n key。 */
  readonly messageKey: string
  /** 內文的 `{0}`、`{1}`… 位置參數（對應 Unity ShowDialog 的 object[] value）。 */
  readonly params?: readonly (string | number)[]
  /** 按鈕組；省略＝單一確認鈕（只關窗）。 */
  readonly actions?: readonly DialogAction[]
  /** 是否顯示右上關閉鈕（Unity showCloseBtn，預設 true）。 */
  readonly showCloseButton?: boolean
  /** 是否可點遮罩關窗（Unity needCloseByTapBG，預設 false）。 */
  readonly closeByTapBackground?: boolean
}

/** Unity CommonDefine 的 Dialog 表 key（前綴為本專案 i18n 的命名空間）。 */
const KEY_SYSTEM_TITLE = 'dialog.System_Title'
const KEY_CONFIRM = 'dialog.Btn_Confirm'
const KEY_ERROR_CODE_PREFIX = 'dialog.Error_Code_'

let view: SystemDialog | null = null
/** view 尚未建立時暫存的請求（對應 Unity DialogMediator._pendingAction 的 lazy-load 佇列）。 */
let pending: DialogRequest | null = null

/** 錯誤碼對應的內文 key（Unity：字串串接 "Error_Code_" + code，無 switch/dictionary）。 */
export function errorCodeMessageKey(code: number): string {
  return `${KEY_ERROR_CODE_PREFIX}${code}`
}

/**
 * 查表；查無此 key 時回傳去掉命名空間的原 key（對應 Unity `entry?.Value ?? key` 的 fallback，
 * 未定義的錯誤碼會直接把 `Error_Code_9999` 顯示在畫面上）。
 */
export function resolveDialogText(key: string, params?: readonly (string | number)[]): string {
  const args: Record<string, string | number> = {}
  params?.forEach((value, index) => (args[index] = value))
  const text = t(key, args)
  return text === key ? key.replace(/^dialog\./, '') : text
}

/** 註冊唯一的對話框 view（composition root 於建立 view 後呼叫）；有排隊中的請求會立即補顯示。 */
export function registerSystemDialog(dialog: SystemDialog): void {
  view = dialog
  if (pending !== null) {
    const queued = pending
    pending = null
    showDialog(queued)
  }
}

/** 測試用：解除註冊並清空佇列。 */
export function resetSystemDialog(): void {
  view = null
  pending = null
}

/** 目前是否有對話框顯示中。 */
export function isDialogOpen(): boolean {
  return view?.isOpen ?? false
}

/** 關閉對話框（對應 DialogMediator.HideDialog）。 */
export function hideDialog(): void {
  view?.hideDialog()
}

/** 顯示對話框（對應 DialogMediator.ShowDialog）。view 尚未就緒時排隊，待註冊後補顯示。 */
export function showDialog(request: DialogRequest): void {
  if (view === null) {
    logger.warn(`對話框尚未就緒，暫存請求：${request.messageKey}`)
    pending = request
    return
  }
  const actions: readonly DialogAction[] =
    request.actions !== undefined && request.actions.length > 0
      ? request.actions
      : [{ text: resolveDialogText(KEY_CONFIRM) }]

  view.showDialog(
    {
      title: resolveDialogText(request.titleKey ?? KEY_SYSTEM_TITLE),
      message: resolveDialogText(request.messageKey, request.params),
      actions,
      showCloseButton: request.showCloseButton ?? true,
      closeByTapBackground: request.closeByTapBackground ?? false,
    },
    (index) => {
      // Unity DialogMediator.OnClickButton：先關窗再跑 action（音效由 AudioManager 另行處理）。
      hideDialog()
      actions[index]?.action?.()
    },
    () => hideDialog(),
  )
}

/**
 * 錯誤碼提示（對應 BaseGameService.ShowErrorCode）：單一確認鈕、無關閉鈕，
 * 按下後等同 Unity 的 ErrorLogEvent → 回大廳／重整頁面。
 */
export function showErrorCode(code: number): void {
  showDialog({
    messageKey: errorCodeMessageKey(code),
    params: [code],
    actions: [{ text: resolveDialogText(KEY_CONFIRM), action: returnToLobby }],
    showCloseButton: false,
  })
}

/**
 * 一般錯誤提示（對應 BaseGameService/GameServerHandler.ShowError）：
 * @param messageKey Dialog 表的錯誤 key，如 dialog.Error_Disconnect／dialog.Error_Unknown
 */
export function showError(messageKey: string): void {
  showDialog({
    messageKey,
    actions: [{ text: resolveDialogText(KEY_CONFIRM), action: returnToLobby }],
    showCloseButton: false,
  })
}

/**
 * 提示但不重整（對應 GameEventDispatcher 的餘額不足窗：OK 只 HideDialog）。
 * @param messageKey 如 dialog.Error_Balance
 */
export function showNotice(messageKey: string): void {
  showDialog({ messageKey, showCloseButton: false })
}

/**
 * 顯示對話框並等待使用者按下唯一的確認鈕（對應 Unity ShowMaxWin 用 TaskCompletionSource 等待的用法）。
 * @param messageKey 內文 key
 * @param params 內文位置參數
 */
export function showConfirm(messageKey: string, params?: readonly (string | number)[]): Promise<void> {
  return new Promise<void>((resolve) => {
    showDialog({
      messageKey,
      ...(params !== undefined ? { params } : {}),
      actions: [{ text: resolveDialogText(KEY_CONFIRM), action: () => resolve() }],
      showCloseButton: false,
    })
  })
}

/** Unity ErrorLogEvent → MenuWindowManager.OnReturnTo 的網頁對應（無平台 HomeUrl，故整頁重載）。 */
function returnToLobby(): void {
  window.location.reload()
}
